import { z } from 'zod/v4';

import { bidSizeRangesSchema } from '../shared/bid-size-schema';
import { getScoreboardQuerySchema } from '../vendors/vendors.schema';

// The sensitivity series reuses the scoreboard's vendor + filter shape (vendorId,
// states/owners/counties/districts/categories/workTypes, date range) and adds the
// margin sweep bounds. The series is computed for every margin in
// [minMargin, maxMargin] stepping by marginStep, across all-time and per year.
export const vendorSensitivityQuerySchema = getScoreboardQuerySchema.extend({
  // The sweep window is [−4%, +8%] — a 4% price cut through an 8% raise. Bounds
  // match that contract; the service clamps as a safety net regardless.
  minMargin: z.coerce.number().int().min(-4).max(8).default(0),
  maxMargin: z.coerce.number().int().min(1).max(8).default(8),
  // Half-percent sweep: 0, 0.5, 1, … (fractional, so not .int()).
  marginStep: z.coerce.number().min(0.5).max(25).default(0.5),
});

// ── Bid-level simulated drill-down ────────────────────────────────────────────

// Coerce a comma string or array into a string[] (URL params arrive as strings,
// JSON bodies as arrays).
const stringArraySchema = z.preprocess((val) => {
  if (Array.isArray(val)) {
    return val;
  }
  if (typeof val === 'string') {
    return val.split(',').filter(Boolean);
  }
  return undefined;
}, z.array(z.string()).optional());

// The toolbar sort, forwarded as a JSON string. Unknown column ids are dropped
// downstream by the repository's ORDER BY whitelist, so this stays permissive.
const simulatedBidsSortSchema = z
  .string()
  .optional()
  .transform((val) => {
    const fallback = [{ id: 'lettingDate', desc: true }];
    if (!val) {
      return fallback;
    }
    try {
      const result = z
        .array(z.object({ id: z.string(), desc: z.boolean() }))
        .safeParse(JSON.parse(val));
      return result.success ? result.data : fallback;
    } catch {
      return fallback;
    }
  });

// The bid-set filter a sensitivity config carries. Same vocabulary as the page
// filters, but values arrive as real JSON (POST body) so no comma-split coercion
// is needed.
export const simulationFiltersSchema = z.object({
  states: z.array(z.string()).nullish(),
  owners: z.array(z.string()).nullish(),
  counties: z.array(z.string()).nullish(),
  districts: z.array(z.string()).nullish(),
  categories: z.array(z.string()).nullish(),
  contractTypes: z.array(z.string()).nullish(),
  projectTypes: z.array(z.string()).nullish(),
  workTypes: z.array(z.string()).nullish(),
  bidSizes: bidSizeRangesSchema,
  minBidAmount: z.number().nullish(),
  maxBidAmount: z.number().nullish(),
  minCostEstimate: z.number().nullish(),
  maxCostEstimate: z.number().nullish(),
  startDate: z.string().nullish(),
  endDate: z.string().nullish(),
});

// One (filter set + margin %) segment of a simulation. `marginPercent` is a
// signed percent applied on top of the bid (e.g. 3 → +3%, -2 → −2%). At
// simulation time configs are evaluated in `position` order; the first whose
// `filters` match a bid assigns its margin, else 0%.
export const simulationConfigSchema = z.object({
  name: z.string().optional(),
  marginPercent: z.coerce.number(),
  position: z.coerce.number().int().optional().default(0),
  filters: simulationFiltersSchema.nullish(),
});

// The bid-level simulated list driven by the simulated CTE: every bid re-ranked
// at the `configs` margin, with the before/after columns the drill renders. The
// filter vocabulary mirrors the scoreboard (geography + date the drill uses);
// `multiBidderOnly` matches the sweep, which excludes single-bidder wins.
export const getVendorSimulatedBidsQuerySchema = z.object({
  vendorId: z.string().uuid().optional(),
  // Multi-vendor scope (My Bids runs the analysis across all of the org's own
  // vendors). Merged with the single `vendorId` into one IN filter server-side.
  vendorIds: stringArraySchema,
  limit: z.coerce.number().min(1).max(100).optional().default(25),
  offset: z.coerce.number().min(0).optional().default(0),
  sort: simulatedBidsSortSchema,
  states: stringArraySchema,
  owners: stringArraySchema,
  counties: stringArraySchema,
  districts: stringArraySchema,
  categories: stringArraySchema,
  contractTypes: stringArraySchema,
  projectTypes: stringArraySchema,
  workTypes: stringArraySchema,
  bidSizes: bidSizeRangesSchema,
  minBidAmount: z.coerce.number().optional(),
  maxBidAmount: z.coerce.number().optional(),
  minCostEstimate: z.coerce.number().optional(),
  maxCostEstimate: z.coerce.number().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  wonOnly: z.coerce.boolean().optional(),
  lostOnly: z.coerce.boolean().optional(),
  // The bottom list's Won / Lost tabs use the ACTUAL (today's) outcome so the
  // per-tab counts don't move as the margin slider changes: `actualWonOnly` =
  // the vendor was the low bidder today; `actualLostOnly` = they were not.
  actualWonOnly: z.coerce.boolean().optional(),
  actualLostOnly: z.coerce.boolean().optional(),
  // The "now losing" segment: bids that won at 0% but lose once marked up
  // (original_winner AND NOT winner).
  flippedToLossOnly: z.coerce.boolean().optional(),
  // The "excluded" segment: ONLY the user-excluded bids (the analysis drops
  // these everywhere else; this opt-in surfaces them so they can be found and
  // restored).
  excludedOnly: z.coerce.boolean().optional(),
  multiBidderOnly: z.coerce.boolean().optional(),
  configs: z.array(simulationConfigSchema).optional(),
  // List-only refinements (the drill + bottom table) — they narrow the table
  // WITHOUT touching the analysis: free-text search and table-local filters
  // (date / states / owners / counties / districts) hard-limited in the UI to the
  // page's applied filters and layered on top of them.
  search: z.string().optional(),
  localStates: stringArraySchema,
  localOwners: stringArraySchema,
  localCounties: stringArraySchema,
  localDistricts: stringArraySchema,
  localStartDate: z.string().optional(),
  localEndDate: z.string().optional(),
});

// The export form of the simulated-bids list: the caller passes a single signed
// `margin` and an outcome `segment` instead of the raw `configs` array + segment
// flags. The export builder expands those into a one-config simulation and the
// matching `*Only` flag, so the CSV/Excel mirrors exactly what the drill / bottom
// Bids table show. Geography/date filters are inherited from the simulated-bids
// schema (comma-split friendly for GET query params).
export const exportVendorSimulatedBidsQuerySchema =
  getVendorSimulatedBidsQuerySchema
    // Drop the raw simulation knobs — the builder derives them from `margin` +
    // `segment`, so accepting them here would let a stray querystring flag (e.g.
    // `actualWonOnly` with `segment=all`) diverge from the requested segment.
    .omit({
      configs: true,
      multiBidderOnly: true,
      wonOnly: true,
      lostOnly: true,
      actualWonOnly: true,
      actualLostOnly: true,
      flippedToLossOnly: true,
      excludedOnly: true,
    })
    .extend({
      margin: z.coerce.number().default(0),
      segment: z
        .enum(['all', 'won', 'lost', 'winning', 'losing', 'excluded'])
        .default('all'),
    });

// Toggle a bid exclusion for a vendor's sensitivity analysis (per org × global
// vendor). `vendorId` is the global vendor id; `bidId` the bid to exclude/restore.
export const vendorBidExclusionSchema = z.object({
  vendorId: z.string().uuid(),
  bidId: z.string().uuid(),
});

// The concentration rollup over the same simulated set: top areas (county) and
// top job types (work types) by the segment's headline $ — added profit for
// the winning segment, missed revenue for the losing segment.
export const getVendorSimulatedConcentrationQuerySchema =
  getVendorSimulatedBidsQuerySchema.extend({
    metric: z.enum(['addedProfit', 'missedRevenue']).default('addedProfit'),
  });
